<script setup>
import { ref, computed } from 'vue'
import { useSavedPlaces } from '@/composables/useSavedPlaces'

const props = defineProps({
  visible: Boolean,
  activeSalida: { type: Object, default: null },
  userLatLng: { type: Array, default: null },
  territoryMeetingPoints: { type: Array, default: () => [] },
})
const emit = defineEmits(['update:visible'])

const reset = () => {
  addingPlace.value = false
  newPlaceName.value = ''
}

const close = () => {
  emit('update:visible', false)
  setTimeout(reset, 300)
}

// ─── Opciones de destino ──────────────────────────────────────────────────────
const pe = computed(() => {
  const p = props.activeSalida?.puntoEncuentro
  if (!p?.latlng) return null
  const ll = p.latlng
  return { name: p.name || p.direccion || '', direccion: p.direccion || '', lat: ll.lat ?? ll[0], lng: ll.lng ?? ll[1] }
})

const pet = computed(() => {
  const p = props.activeSalida?.puntoEncuentroTerritorio
  if (!p?.latlng) return null
  const ll = p.latlng
  return { name: p.name || p.direccion || '', direccion: p.direccion || '', lat: ll.lat ?? ll[0], lng: ll.lng ?? ll[1] }
})

// Puntos de encuentro de los territorios asignados (salidas "De Grupo Continua")
const terrPoints = computed(() =>
  (props.territoryMeetingPoints || [])
    .filter((p) => p.latlng)
    .map((p) => {
      const ll = p.latlng
      return {
        name: p.name || p.direccion || '',
        direccion: p.direccion || '',
        terrKey: p.terrKey || '',
        lat: ll.lat ?? ll[0],
        lng: ll.lng ?? ll[1],
      }
    })
)

// ─── Lugares guardados ────────────────────────────────────────────────────────
const { places: savedPlaces, add: addPlace, remove: removePlace } = useSavedPlaces()

const addingPlace = ref(false)
const newPlaceName = ref('')

const deletePlace = (idx) => removePlace(idx)

const saveCurrentPlace = () => {
  const name = newPlaceName.value.trim()
  if (!name || !props.userLatLng) return
  addPlace(name, props.userLatLng[0], props.userLatLng[1])
  addingPlace.value = false
  newPlaceName.value = ''
}

// ─── Seleccionar destino: abre directamente el GPS (Google Maps) ────────────
const selectDest = (dest) => {
  const target = `${dest.lat},${dest.lng}`
  const url = props.userLatLng
    ? `https://www.google.com/maps/dir/?api=1&origin=${props.userLatLng[0]},${props.userLatLng[1]}&destination=${target}`
    : `https://www.google.com/maps?q=${target}`
  window.open(url, '_blank')
  close()
}
</script>

<template>
  <Transition name="nav-overlay" :duration="{ enter: 350, leave: 300 }">
    <div v-if="visible" class="nav-overlay">
      <div class="nav-backdrop" @click="close" />

      <div class="nav-sheet">
        <div class="drag-handle" />

        <div class="sheet-title-row">
          <h2 class="sheet-title">¿A dónde vas?</h2>
          <button class="close-btn" @click="close"><i class="pi pi-times" /></button>
        </div>

        <div class="dest-list">
          <!-- Punto de encuentro -->
          <button v-if="pe" class="dest-row" @click="selectDest(pe)">
            <span class="dest-icon-wrap dest-icon-green">
              <i class="pi pi-map-marker" />
            </span>
            <div class="dest-info">
              <span class="dest-label">Punto de encuentro</span>
              <span class="dest-name">{{ pe.name }}</span>
            </div>
            <i class="pi pi-chevron-right dest-chevron" />
          </button>

          <!-- Punto en territorio -->
          <button v-if="pet" class="dest-row" @click="selectDest(pet)">
            <span class="dest-icon-wrap dest-icon-blue">
              <i class="pi pi-flag" />
            </span>
            <div class="dest-info">
              <span class="dest-label">Punto en territorio</span>
              <span class="dest-name">{{ pet.name }}</span>
            </div>
            <i class="pi pi-chevron-right dest-chevron" />
          </button>

          <!-- Puntos de encuentro de los territorios asignados -->
          <button
            v-for="(p, idx) in terrPoints"
            :key="'terr-' + idx"
            class="dest-row"
            @click="selectDest(p)"
          >
            <span class="dest-icon-wrap dest-icon-green">
              <i class="pi pi-map-marker" />
            </span>
            <div class="dest-info">
              <span class="dest-label" v-if="p.terrKey">Territorio {{ p.terrKey }}</span>
              <span class="dest-name">{{ p.name }}</span>
            </div>
            <i class="pi pi-chevron-right dest-chevron" />
          </button>

          <!-- Lugares guardados -->
          <div
            v-for="(place, idx) in savedPlaces"
            :key="idx"
            class="dest-row dest-row-saved"
          >
            <button class="dest-row-inner" @click="selectDest(place)">
              <span class="dest-icon-wrap dest-icon-gray">
                <i class="pi pi-home" />
              </span>
              <div class="dest-info">
                <span class="dest-name">{{ place.name }}</span>
              </div>
              <i class="pi pi-chevron-right dest-chevron" />
            </button>
            <button class="dest-delete-btn" @click="deletePlace(idx)">
              <i class="pi pi-times" />
            </button>
          </div>

          <!-- Agregar lugar con ubicación actual -->
          <div class="add-place-wrap">
            <template v-if="!addingPlace">
              <button class="dest-row dest-row-add" @click="addingPlace = true">
                <span class="dest-icon-wrap dest-icon-gray">
                  <i class="pi pi-plus" />
                </span>
                <span class="dest-add-label">Crear nuevo lugar</span>
              </button>
            </template>
            <template v-else>
              <div class="add-place-form">
                <input
                  v-model="newPlaceName"
                  class="place-input"
                  type="text"
                  placeholder="Nombre del lugar (ej: Alojamiento)"
                  enterkeyhint="done"
                  @keyup.enter="saveCurrentPlace"
                  autofocus
                />
                <div v-if="!userLatLng" class="no-location-hint">
                  <i class="pi pi-info-circle" />
                  Activá <strong>Ubicación</strong> en el mapa para guardar tu posición actual
                </div>
                <div class="add-place-actions">
                  <button class="btn-cancel-add" @click="addingPlace = false; newPlaceName = ''">Cancelar</button>
                  <button
                    class="btn-save-place"
                    :disabled="!newPlaceName.trim() || !userLatLng"
                    @click="saveCurrentPlace"
                  >Guardar</button>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.nav-overlay {
  position: fixed;
  inset: 0;
  z-index: 2500;
}

.nav-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
}

.nav-sheet {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--bg-grouped);
  border-radius: 20px 20px 0 0;
  padding: 8px 0;
  padding-bottom: max(env(safe-area-inset-bottom, 16px), 16px);
  box-shadow: 0 -4px 30px rgba(0, 0, 0, 0.2);
  max-height: 85vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.drag-handle {
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: rgba(var(--label-rgb), 0.25);
  margin: 4px auto 10px;
}

.sheet-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 16px 12px;
}

.sheet-title {
  font-size: 22px;
  font-weight: 700;
  color: var(--label-primary);
  margin: 0;
  letter-spacing: -0.3px;
}

.close-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(var(--label-rgb), 0.12);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(var(--label-rgb), 0.6);
  font-size: 0.75rem;
  -webkit-tap-highlight-color: transparent;
  position: relative;
}

.close-btn::after {
  content: '';
  position: absolute;
  inset: -8px;
}

/* Lista de destinos */
.dest-list {
  background: var(--bg-primary);
  margin: 0 16px 12px;
  border-radius: 14px;
  overflow: hidden;
}

.dest-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 14px;
  background: none;
  border: none;
  border-bottom: 1px solid rgba(var(--label-rgb), 0.1);
  cursor: pointer;
  width: 100%;
  text-align: left;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.1s;
}

.dest-row:last-child {
  border-bottom: none;
}

.dest-row:active {
  background: rgba(0, 0, 0, 0.04);
}

.dest-row-saved {
  padding: 0;
  display: flex;
  align-items: center;
}

.dest-row-inner {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 14px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  -webkit-tap-highlight-color: transparent;
}

.dest-delete-btn {
  flex-shrink: 0;
  background: none;
  border: none;
  color: rgba(var(--label-rgb), 0.4);
  font-size: 0.75rem;
  padding: 13px 14px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.dest-row-add {
  color: var(--accent);
}

.dest-add-label {
  font-size: 16px;
  font-weight: 500;
  color: var(--accent);
}

.dest-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  flex-shrink: 0;
  color: #fff;
}

.dest-icon-green { background: var(--success); }
.dest-icon-blue  { background: var(--accent); }
.dest-icon-gray  { background: rgba(var(--label-rgb), 0.15); color: rgba(var(--label-rgb),0.6); }

.dest-info {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.dest-label {
  font-size: 12px;
  color: rgba(var(--label-rgb), 0.5);
  margin-bottom: 1px;
}

.dest-name {
  font-size: 16px;
  font-weight: 500;
  color: var(--label-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dest-chevron {
  color: rgba(var(--label-rgb), 0.3);
  font-size: 0.75rem;
  flex-shrink: 0;
}

/* Agregar lugar */
.add-place-wrap {
  border-top: 1px solid rgba(var(--label-rgb), 0.1);
}

.add-place-form {
  padding: 12px 14px;
}

.place-input {
  display: block;
  width: 100%;
  padding: 12px 14px;
  border-radius: 10px;
  background: var(--bg-grouped);
  border: none;
  font-size: 16px;
  color: var(--label-primary);
  outline: none;
  margin-bottom: 10px;
  box-sizing: border-box;
  -webkit-appearance: none;
}

.place-input::placeholder {
  color: rgba(var(--label-rgb), 0.35);
}

.no-location-hint {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 13px;
  color: rgba(var(--label-rgb), 0.55);
  margin-bottom: 10px;
  line-height: 1.4;
}

.no-location-hint i {
  font-size: 0.75rem;
  margin-top: 1px;
  flex-shrink: 0;
}

.add-place-actions {
  display: flex;
  gap: 10px;
}

.btn-cancel-add {
  flex: 1;
  padding: 11px;
  background: rgba(var(--label-rgb), 0.1);
  border: none;
  border-radius: 10px;
  font-size: 16px;
  color: rgba(var(--label-rgb), 0.7);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.btn-save-place {
  flex: 1;
  padding: 11px;
  background: var(--accent);
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: opacity 0.12s;
}

.btn-save-place:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Transiciones */
.nav-overlay-enter-active .nav-sheet,
.nav-overlay-leave-active .nav-sheet {
  transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1);
}
.nav-overlay-enter-active .nav-backdrop,
.nav-overlay-leave-active .nav-backdrop {
  transition: opacity 0.3s ease;
}
.nav-overlay-enter-from .nav-sheet,
.nav-overlay-leave-to .nav-sheet {
  transform: translateY(100%);
}
.nav-overlay-enter-from .nav-backdrop,
.nav-overlay-leave-to .nav-backdrop {
  opacity: 0;
}
</style>
